import { useApp } from "@/context/AppContext";
import { useSEO } from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Smartphone, Tablet, Monitor } from "lucide-react";
import { KEYWORDS } from "@/lib/seoContent";
import {
  webPageLd,
  breadcrumbLd,
  organizationLd,
  caseStudyLd,
} from "@/lib/structuredData";

const ZEMKA_IMG =
  "https://customer-assets.emergentagent.com/job_vibe-showcase-4/artifacts/i6afm4d1_1d5966b5-cb88-432d-95ce-833443653f42.png";

const DeviceMockup = ({ variant = "desktop", altText, label, testid }) => {
  const frames = {
    desktop: { wrapper: "aspect-[16/10]", icon: Monitor, fit: "object-contain" },
    tablet: { wrapper: "aspect-[3/4]", icon: Tablet, fit: "object-contain" },
    mobile: { wrapper: "aspect-[9/16]", icon: Smartphone, fit: "object-contain" },
  };
  const f = frames[variant];
  const Icon = f.icon;
  return (
    <figure
      data-testid={testid}
      className="relative border border-[#0A0A0A] bg-white shadow-[6px_6px_0px_#0A0A0A]"
    >
      <div className={`${f.wrapper} overflow-hidden bg-[#0F2A44]`}>
        <img
          src={ZEMKA_IMG}
          alt={altText}
          className={`w-full h-full ${f.fit}`}
          loading="lazy"
        />
      </div>
      <figcaption className="flex items-center justify-between px-4 py-3 border-t border-[#0A0A0A]/20">
        <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold">
          <Icon className="h-3.5 w-3.5 text-[#3A00FF]" aria-hidden />
          {label}
        </span>
        <span className="text-xs text-[#525252]">zemka.de</span>
      </figcaption>
    </figure>
  );
};

const Portfolio = () => {
  const { t, lang } = useApp();
  useSEO({
    title: t.portfolio.metaTitle,
    description: t.portfolio.metaDesc,
    path: "/portfolio",
    lang,
    keywords: KEYWORDS[lang].portfolio,
    jsonLd: [
      organizationLd(),
      webPageLd({
        path: "/portfolio",
        name: t.portfolio.metaTitle,
        description: t.portfolio.metaDesc,
        lang,
        type: "CollectionPage",
      }),
      caseStudyLd({
        path: "/portfolio",
        headline: t.portfolio.title,
        description: t.portfolio.subtitle,
        lang,
      }),
      breadcrumbLd([
        { name: t.nav.home, path: "/" },
        { name: t.nav.portfolio, path: "/portfolio" },
      ]),
    ],
  });

  return (
    <main data-testid="portfolio-page" className="pt-16 lg:pt-20">
      {/* HERO */}
      <section>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-16 lg:pb-24">
          <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
            <span className="inline-block h-[1px] w-8 bg-[#3A00FF]" />
            {t.portfolio.eyebrow}
          </div>
          <h1
            data-testid="portfolio-title"
            className="mt-6 text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.9] tracking-tighter font-black text-[#0A0A0A] font-[Cabinet_Grotesk]"
          >
            {t.portfolio.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#525252]">
            {t.portfolio.subtitle}
          </p>
        </div>
      </section>

      {/* OVERVIEW / CHALLENGE / SOLUTION / RESULTS */}
      <section className="border-t border-[#0A0A0A] bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {[
            { eyebrow: "01", title: t.portfolio.overview, body: t.portfolio.overviewBody },
            { eyebrow: "02", title: t.portfolio.challenges, body: t.portfolio.challengesBody },
            { eyebrow: "03", title: t.portfolio.solution, body: t.portfolio.solutionBody },
            { eyebrow: "04", title: t.portfolio.results, body: t.portfolio.resultsBody },
          ].map((block, i) => (
            <article
              key={i}
              data-testid={`case-block-${i}`}
              className="lg:col-span-6 border border-[#0A0A0A] p-8 lg:p-10 bg-white"
            >
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#3A00FF]">
                {block.eyebrow}
              </div>
              <h2 className="mt-3 text-2xl lg:text-3xl tracking-tight font-black font-[Cabinet_Grotesk]">
                {block.title}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-[#525252]">
                {block.body}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* DEVICE MOCKUPS */}
      <section className="border-t border-[#0A0A0A] bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
            <span className="inline-block h-[1px] w-8 bg-[#3A00FF]" />
            {t.portfolio.devicesEyebrow}
          </div>
          <h2 className="mt-4 max-w-3xl text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
            {t.portfolio.devicesTitle}
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-end">
            <div className="md:col-span-7">
              <DeviceMockup
                variant="desktop"
                label="Desktop"
                testid="mockup-desktop"
                altText="Zemka Gebäudereinigung desktop website screenshot — professional business website by NetoVibe"
              />
            </div>
            <div className="md:col-span-3">
              <DeviceMockup
                variant="tablet"
                label="Tablet"
                testid="mockup-tablet"
                altText="Zemka Gebäudereinigung tablet layout screenshot — responsive custom website development"
              />
            </div>
            <div className="md:col-span-2">
              <DeviceMockup
                variant="mobile"
                label="Mobile"
                testid="mockup-mobile"
                altText="Zemka Gebäudereinigung mobile view screenshot — mobile-first design for a local cleaning company"
              />
            </div>
          </div>
        </div>
      </section>

      {/* MORE + CTA */}
      <section className="border-t border-[#0A0A0A] bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#3A00FF]">
              {t.portfolio.moreEyebrow}
            </div>
            <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter font-black font-[Cabinet_Grotesk]">
              {t.portfolio.moreTitle}
            </h2>
            <p className="mt-6 max-w-xl text-base text-white/70">
              {t.portfolio.moreLead}
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link to="/contact">
              <Button
                data-testid="portfolio-cta"
                className="h-14 px-8 rounded-none bg-white text-[#0A0A0A] hover:bg-[#3A00FF] hover:text-white text-xs tracking-widest uppercase font-bold"
              >
                {t.nav.cta}
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Portfolio;
